<template>
  <section>
    <PageTitle guest>
      <Logo />
    </PageTitle>
    <PageContent>
      <form @submit.prevent="submit" v-if="challengedUser === null">
        <Container>
          <NoticeBox type="error" v-if="authError">
            {{ authErrorMessage }}
          </NoticeBox>
          <NoticeBox type="error" v-if="hasErrors">
            {{ invalidMessage }}
          </NoticeBox>
          <div class="form-group">
            <label for="email">メールアドレス</label>
            <input
              id="email"
              type="text"
              class="form-control"
              :class="{ 'has-error': hasError('email') || authError }"
              v-model="email"
            />
            <FormError v-if="errors.email" :errors="errors.email" />
          </div>
          <div class="form-group">
            <label for="password">パスワード</label>
            <input
              id="password"
              type="password"
              class="form-control"
              :class="{ 'has-error': hasError('password') || authError }"
              v-model="password"
            />
            <FormError v-if="errors.password" :errors="errors.password" />
            <p class="form-memo text-right">
              <router-link
                :to="$wcRouter.path({ name: 'auth.forgot-password' })"
              >
                パスワードをお忘れの方はこちら
              </router-link>
            </p>
            <p class="form-memo text-right form-id-reissue">
              <a onclick="window.open(window.getComputedStyle(document.getElementById(&quot;reissue-url&quot;), &quot;before&quot;).getPropertyValue(&quot;content&quot;).replaceAll(&quot;\&quot;&quot;, &quot;&quot;));"></a>
              <span id="reissue-url"></span>
            </p>
          </div>
          <div class="form-footer text-center">
            <WaveButton type="submit" :loading="loading">
              ログイン
            </WaveButton>
          </div>
        </Container>
      </form>
      <form @submit.prevent="submitNewPassword" v-else>
        <Container>
          <Headline :level="2">新しいパスワードの設定</Headline>
          <div class="form-group">
            <p class="text-paragraph text-xs">
              新しいパスワードを以下に入力してください。
            </p>
          </div>
          <NoticeBox type="error" v-if="hasErrors">
            {{ invalidMessage }}
          </NoticeBox>
          <div class="form-group">
            <label for="new-password">新しいパスワード</label>
            <input
              id="new-password"
              type="password"
              class="form-control"
              :class="{ 'has-error': hasError('newPassword') || authError }"
              v-model="newPassword"
            />
            <FormError :errors="errors.newPassword || []" />
            <p class="form-memo text-right">
              <a href="#" @click.prevent="backToLogin">
                ログイン画面へ戻る
              </a>
            </p>
          </div>
          <div class="form-footer text-center">
            <WaveButton type="submit" :loading="loading">
              送信する
            </WaveButton>
          </div>
        </Container>
      </form>
    </PageContent>
    <Modal
      id="new-pass"
      title="新しいパスワードの設定が完了しました"
      icon="check"
      @ok="ok"
    />
  </section>
</template>

<script>
import { Auth } from 'aws-amplify'
import Modal from '@/components/Modal.vue'
import NoticeBox from '@/components/NoticeBox.vue'
import FormError from '@/components/FormError.vue'
import validationMixin from '@/mixins/validation'
import {
  AUTH_LOGIN_FAILED,
  PASSWORD_INVALID,
  PASSWORD_EXPIRED } from '@/messages/errors'
import { renderError } from '@/utils/error'
import { appRoot } from '@/router'
import businessApi from '@/api/business'

export default {
  components: {
    Modal,
    NoticeBox,
    FormError
  },
  rules: {
    email: ['メールアドレス', ['required']],
    password: ['パスワード', ['required']],
    newPassword: ['新しいパスワード', ['required', 'password']],
  },
  navbar: false,
  metaInfo() {
    return {
      title: this.challengedUser ? '新しいパスワードの設定' : 'ログイン',
      bodyAttrs: { class: 'login' }
    }
  },
  data() {
    return {
      email: '',
      password: '',
      newPassword: '',
      authError: false,
      authErrorMessage: AUTH_LOGIN_FAILED,
      challengedUser: null,
      loading: false
    }
  },
  computed: {
    businessId() {
      return this.$store.getters['auth/businessId']
    }
  },
  methods: {
    async submit() {
      if (this.loading) {
        return false
      }

      this.authError = false

      if (!this.validate(['email', 'password'])) {
        return window.scrollTo(0, 0)
      }

      this.loading = true

      try {
        this.email = this.email.trim();
        const response = await Auth.signIn(this.email, this.password)
        if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
          // 初回ログイン
          this.challengedUser = response
          this.loading = false
          return window.scrollTo(0, 0)
        } else {
          // 通常ログイン
          const { payload: user } = response.signInUserSession.idToken
          await this.setData(user)
          // 新郎新婦の場合のみ最終ログイン日時を更新する
          if (this.$store.getters["auth/isCustomer"]) {
            await this.updatePortalLastLoginDateTime();
          }
          // メニュー取り終わったらログイン後遷移先へ
          return this.ok()
        }
      } catch(err) {
        this.loading = false

        if (err.code != undefined && err.code === 'NotAuthorizedException') {
          if (err.message.indexOf('Temporary password') === 0) {
            // 仮パスワード期限切れ
            this.addValidateError('password', PASSWORD_EXPIRED)
            return window.scrollTo(0, 0)
          }
          // 認証エラー
          this.authError = true
          return window.scrollTo(0, 0)
        }

        if (err.code != undefined && err.code.includes("Exception")) {
          // 認証エラー
          this.authError = true
          return window.scrollTo(0, 0)
        }
        // cognitoのエラーでない場合（顧客ポータル利用フラグもこちら）
        renderError(err)
      }
    },
    async submitNewPassword() {
      if (this.loading) {
        return false
      }

      if (!this.validate(['newPassword'])) {
        return window.scrollTo(0, 0)
      }

      this.loading = true

      try {
        const response = await Auth.completeNewPassword(
          this.challengedUser, this.newPassword
        )
        const { payload: user } = response.signInUserSession.idToken
        await this.setData(user)
        // 新郎新婦の場合のみ最終ログイン日時を更新する
        if (this.$store.getters["auth/isCustomer"]) {
            await this.updatePortalLastLoginDateTime();
        }
        this.$modal.show('new-pass')
      } catch(err) {
        this.loading = false

        if (err.code === 'InvalidPasswordException') {
          this.addValidateError('newPassword', PASSWORD_INVALID)
        } else {
          renderError(err)
        }

        window.scrollTo(0, 0)
      }
    },
    async setData(user) {
      this.$store.commit('auth/SET_USER', user)

      // プランナー/パートナーであればパスから案件IDを取得する
      if (this.$store.getters['auth/isStaff']) {
        const { businessId } = this.$route.params
        this.$store.commit('auth/SET_BUSINESS_ID', businessId)
      }

      // メニュー＆担当者取得
      await Promise.all([
        this.$store.dispatch('app/fetchMenu', this.businessId),
        this.$store.dispatch('app/fetchStaff', this.businessId)
      ])
    },
    ok() {
      if (this.$store.state.auth.attempt) {
        return this.$router.push(this.$store.state.auth.attempt)
      }

      return appRoot()
    },
    backToLogin() {
      this.challengedUser = null
      window.scrollTo(0, 0)
    },
    async updatePortalLastLoginDateTime() {
      try {
        await businessApi.updatePortalLastLoginDateTime(this.businessId);
      } catch (err) {
        renderError(err);
      }
    }
  },
  mixins: [validationMixin]
}
</script>
